const componentName = 'c-JSONCodeBlock';

/**
 * Unescapes first children of JSON object if it's an escaped object or array.
 */
const unescapeJSON = (obj: Record<string, any>) => {
  for (const [key, value] of Object.entries(obj)) {
    if (
      typeof value === 'string' &&
      (value.startsWith('{') || value.startsWith('['))
    ) {
      try {
        obj[key] = JSON.parse(value);
      } catch (_) {
        // Do nothing
      }
    }
  }
};

const removeQuotes = (str: string | undefined) => {
  if (!str) {
    return '';
  }

  return str.replace(/["': ]+/g, '');
};

/**
 * Replaces a JSON line with a pretty printed line.
 */
const replacer = (
  errorAttributes: string[],
  _match: string,
  pIndent: string | undefined,
  pKey: string | undefined,
  pVal: string | undefined,
  pEnd: string | undefined,
) => {
  const indentation = pIndent || '';
  const keyWithError = errorAttributes.includes(removeQuotes(pKey));

  const key = pKey
    ? `<span style="${
        keyWithError ? 'color:var(--error);' : ''
      }" class="${`${componentName}__key`}">${pKey.replace(': ', '')}</span>: `
    : '';

  const val = (() => {
    if (!pVal) {
      return '';
    }

    const valueType = (() => {
      if (pVal[0] === '"') {
        return 'string';
      }

      if (pVal === 'true' || pVal === 'false') {
        return 'boolean';
      }

      if (pVal === 'null') {
        return 'null';
      }

      return 'number';
    })();

    return `<span className="${`${componentName}__${valueType}`}">${pVal}</span>`;
  })();

  return `${indentation}${key}${val}${pEnd || ''}`;
};

/**
 * Pretty prints a JSON object.
 */
export const prettyPrint = (
  obj: Record<string, any>,
  errorAttributes: string[],
) => {
  const jsonLine =
    /^( *)("[^"]+": )?("[^"]*"|[\w.+-]*)?([,[{]|\[\s*\],?|\{\s*\},?)?$/gm;

  unescapeJSON(obj);

  return {
    __html: JSON.stringify(obj, null, 2)
      .replace(/&/g, '&amp;')
      .replace(/\\"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(jsonLine, replacer.bind(null, errorAttributes)),
  };
};

export const formatXML = (xml: string, indent = '    '): string => {
  const splitOnTags = (str: string): string[] =>
    str.split(/(<\/?[^>]+>)/g).filter(line => line.trim() !== '');
  const isTag = (str: string): boolean => /<[^>!]+>/.test(str);
  const isXMLDeclaration = (str: string): boolean => /<\?[^?>]+\?>/.test(str);
  const isClosingTag = (str: string): boolean => /<\/+[^>]+>/.test(str);
  const isSelfClosingTag = (str: string): boolean => /<[^>]+\/>/.test(str);
  const isOpeningTag = (str: string): boolean =>
    isTag(str) &&
    !isClosingTag(str) &&
    !isSelfClosingTag(str) &&
    !isXMLDeclaration(str);

  // Function to repeat a string n times without 'repeat-string'
  const repeat = (str: string, times: number): string =>
    new Array(times + 1).join(str);

  let depth = 0;
  let ignoreMode = false;
  let deferred: string[] = [];

  return splitOnTags(xml)
    .map(item => {
      if (item.trim().startsWith('<![CDATA[')) {
        ignoreMode = true;
      }
      if (item.trim().endsWith(']]>')) {
        ignoreMode = false;
        deferred.push(item);
        const cdataBlock = deferred.join('');
        deferred = [];
        return cdataBlock;
      }
      if (ignoreMode) {
        deferred.push(item);
        return null;
      }

      // removes any pre-existing whitespace chars at the end or beginning of the item
      item = item.replace(/^\s+|\s+$/g, '');

      if (isClosingTag(item)) {
        depth--;
      }

      const line = repeat(indent, depth) + item;

      if (isOpeningTag(item)) {
        depth++;
      }

      return line;
    })
    .filter(c => c)
    .join('\n');
};
